import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Grid2 as Grid,
  Button,
  CircularProgress,
  Typography,
  Stack,
} from "@mui/material";
import { StyledFormicError } from "components/Input";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import { ORG_TYPES, Organization } from "types/organization";
import * as Yup from "yup";
import { FC, useState } from "react";
import { useUpdateOrganizationMutation } from "graphql/__generated__/hooks";
import { OrganizationUploadImage } from "components/Input/OrganizationUploadImage";
import { green } from "@mui/material/colors";

export const getValidationSchema = () =>
  Yup.object().shape({
    organizationType: Yup.string().required("This field is required"),
    organizationName: Yup.string().required("This field is required"),
    primaryContactEmail: Yup.string().email("Invalid email address"),
    secondaryContactEmail: Yup.string().email("Invalid email address"),
  });

type OrganizationStepProps = {
  organization: Organization | null;
  onBack?: () => void;
  onNext?: () => void;
};

export const OrganizationStep: FC<OrganizationStepProps> = ({
  organization,
  onBack,
  onNext,
}) => {
  const [img, setImg] = useState("");
  const [updateOrganization, { loading }] = useUpdateOrganizationMutation();
  const initialValues = {
    organizationType: organization?.type || "",
    organizationName: organization?.name || "",
    primaryContactName: organization?.primary_contact_name || "",
    primaryContactEmail: organization?.primary_contact_email || "",
    secondaryContactName: organization?.secondary_contact_name || "",
    secondaryContactEmail: organization?.secondary_contact_email || "",
    logo_url: organization?.logo_url || "",
  };

  const needToBeUpdated = (reqValues: Organization) => {
    return (
      organization?.type !== reqValues.type ||
      organization?.name !== reqValues.name ||
      organization?.primary_contact_name !== reqValues.primary_contact_name ||
      organization?.primary_contact_email !== reqValues.primary_contact_email ||
      organization?.secondary_contact_name !==
        reqValues.secondary_contact_name ||
      organization?.secondary_contact_email !==
        reqValues.secondary_contact_email
    );
  };

  const updateOrganizationData = async (reqValues: Organization) => {
    if (needToBeUpdated(reqValues)) {
      const newOrgResult = await updateOrganization({
        variables: {
          id: organization?.org_id,
          organization: reqValues,
        },
      });
      console.log(newOrgResult);
    }
    onNext?.();
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: getValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const reqValues = {
        type: values.organizationType,
        name: values.organizationName,
        primary_contact_name: values.primaryContactName,
        primary_contact_email: values.primaryContactEmail,
        secondary_contact_name: values.secondaryContactName,
        secondary_contact_email: values.secondaryContactEmail,
        logo_url: img,
      };

      updateOrganizationData(reqValues as Organization).then();
    },
  });

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2 }}>
        Organization Information
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Please provide your organization details
      </Typography>

      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <OrganizationUploadImage onUploadedFile={setImg} img={img} />
            </Grid>
            {/* Primera fila: Organization Type y Organization Name */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="organizationTypeLabel">
                  Organization Type
                </InputLabel>
                <Select
                  labelId="organizationTypeLabel"
                  id="organizationType"
                  name="organizationType"
                  value={formik?.values?.organizationType}
                  onChange={formik.handleChange}
                  label="Organization Type"
                  size="small"
                >
                  {ORG_TYPES.map((type) => (
                    <MenuItem value={type.value} key={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                <ErrorMessage
                  name="organizationType"
                  component={StyledFormicError}
                />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                variant="outlined"
                onChange={formik.handleChange}
                fullWidth
                name="organizationName"
                label="Organization Name"
                type="text"
                id="organizationName"
                value={formik?.values?.organizationName}
                placeholder="Enter organization name"
                size="small"
              />
              <ErrorMessage
                name="organizationName"
                component={StyledFormicError}
              />
            </Grid>

            {/* Segunda fila: Primary Contact Name y Primary Contact Email */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                variant="outlined"
                onChange={formik.handleChange}
                fullWidth
                name="primaryContactName"
                label="Primary Contact Name"
                type="text"
                id="primaryContactName"
                value={formik?.values?.primaryContactName}
                placeholder="Enter primary contact name"
                size="small"
              />
              <ErrorMessage
                name="primaryContactName"
                component={StyledFormicError}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                variant="outlined"
                onChange={formik.handleChange}
                fullWidth
                name="primaryContactEmail"
                label="Primary Contact Email"
                type="email"
                id="primaryContactEmail"
                value={formik?.values?.primaryContactEmail}
                placeholder="Enter primary contact email"
                size="small"
              />
              <ErrorMessage
                name="primaryContactEmail"
                component={StyledFormicError}
              />
            </Grid>

            {/* Tercera fila: Secondary Contact Name y Secondary Contact Email */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                variant="outlined"
                onChange={formik.handleChange}
                fullWidth
                name="secondaryContactName"
                label="Secondary Contact Name"
                type="text"
                id="secondaryContactName"
                value={formik?.values?.secondaryContactName}
                placeholder="Enter secondary contact name"
                size="small"
              />
              <ErrorMessage
                name="secondaryContactName"
                component={StyledFormicError}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                variant="outlined"
                onChange={formik.handleChange}
                fullWidth
                name="secondaryContactEmail"
                label="Secondary Contact Email"
                type="email"
                id="secondaryContactEmail"
                value={formik?.values?.secondaryContactEmail}
                placeholder="Enter secondary contact email"
                size="small"
              />
              <ErrorMessage
                name="secondaryContactEmail"
                component={StyledFormicError}
              />
            </Grid>

            {/* Botones de acción */}
            <Grid size={12} sx={{ mt: 1 }}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="flex-end"
              >
                {onNext && (
                  <Button
                    variant="text"
                    color="primary"
                    onClick={onNext}
                    disabled={loading}
                  >
                    Skip this step and continue
                  </Button>
                )}
                <Button
                  disabled={loading}
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ minWidth: 150 }}
                >
                  Next
                </Button>
                {loading && (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: green[500],
                      position: "absolute",
                      top: "50%",
                      right: "75px",
                      marginTop: "-12px",
                    }}
                  />
                )}
              </Stack>
            </Grid>
          </Grid>
        </form>
      </FormikProvider>
    </Box>
  );
};
